import { Alert, Button, CircularProgress, Link, Paper, Stack, Typography } from '@mui/material';
import { GridDensity, GridPaginationModel, GridRowId } from '@mui/x-data-grid';
import { LabLostAndFoundResponse, LabLostAndFoundUpload, ignoreLabUploads } from 'api/labs';
import { InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import { DEFAULT_READ_ONLY_GRID_FILTER_MODEL, ReadOnlyDataGrid } from 'components/atoms/BaseDataGrid/ReadOnlyDataGrid';
import { CountedToggleButtonGroup } from 'components/atoms/CountedToggleButtonGroup';
import wrapPage from 'components/atoms/wrapPage/wrapPage';
import { useConfirmation } from 'components/modals/ConfirmationContext';
import PageHeader from 'components/PageHeader';
import { Permission } from 'interfaces/permissionOption';
import { UploadDashboardRow } from 'interfaces/fileList';
import { difference, filter, forEach, includes, map, orderBy, some, uniq } from 'lodash';
import { useSnackbar } from 'notistack';
import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { getQueryErrorMessage } from 'utils/httpError';
import { useLabLostAndFound } from 'utils/queryHooks/useLabLostAndFound';
import { useLabUploadSummary } from 'utils/queryHooks/useLabUploadSummary';
import { useStudies } from 'utils/queryHooks/useStudies';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { usePermissions } from 'utils/usePermissions';

import { getManifestDialogUrlForLostUploads } from './helpers';
import { LostAndFoundToolbar } from './LostAndFoundToolbar';
import UploadIngestExecutionDialog from '../UploadsDashboard/UploadIngestExecutionDialog';
import { useLostAndFoundColumns } from './useLostAndFoundColumns';
import {
  LOST_AND_FOUND_MISSING_REASONS,
  missingReasonLabels,
  PAGE_SIZE_OPTIONS,
  useLostAndFoundQueryParams,
} from './useLostAndFoundQueryParams';

const DISABLE_VIRTUALIZATION_FOR_TESTS = process.env.NODE_ENV === 'test';
const DEFAULT_SELECTION_MODEL_STATE: GridRowId[] = [];
const DEFAULT_SELECTED_ROWS_BY_OBJECT_KEY_STATE: Record<string, LabLostAndFoundUpload> = {};

const getUploadFileName = (objectKey: string) => objectKey.split('/').pop() || objectKey;

const toUploadDashboardRow = (row: LabLostAndFoundUpload): UploadDashboardRow => {
  const fileName = getUploadFileName(row.objectKey);
  const extensionSeparatorIndex = fileName.lastIndexOf('.');

  return {
    id: row.objectKey,
    name: fileName,
    fileName,
    size: row.size,
    date: row.uploadedAt,
    type: extensionSeparatorIndex > 0 ? fileName.slice(extensionSeparatorIndex + 1).toLowerCase() : '',
    path: row.objectKey,
    status: row.latestIngestJob?.status,
    objectKey: row.objectKey,
    isHidden: false,
    canHide: false,
    uploadKey: row.objectKey,
    slideId: row.resolvedSlideId,
    latestIngestJob: row.latestIngestJob,
    activeIngestJob: row.activeIngestJob,
    latestFailedIngestJob: row.latestFailedIngestJob,
    latestSuccessfulIngestJob: row.latestSuccessfulIngestJob,
    latestSuccessfulIngestJobArchived: row.latestSuccessfulIngestJobArchived,
    cases: null,
    uploadCategory: 'awaitingIngest',
  };
};

const removeUploadsFromResponse = (
  currentResponse: LabLostAndFoundResponse | undefined,
  objectKeys: string[]
): LabLostAndFoundResponse | undefined => {
  if (!currentResponse) {
    return currentResponse;
  }

  const remainingUploads = filter(currentResponse.lostUploads, ({ objectKey }) => !includes(objectKeys, objectKey));
  return {
    ...currentResponse,
    lostUploads: remainingUploads,
  };
};

const removeUploadsFromInfiniteResponse = (
  currentResponse: InfiniteData<LabLostAndFoundResponse> | undefined,
  objectKeys: string[]
): InfiniteData<LabLostAndFoundResponse> | undefined =>
  currentResponse
    ? {
        ...currentResponse,
        pages: map(currentResponse.pages, (page) => removeUploadsFromResponse(page, objectKeys)!),
      }
    : currentResponse;

export const LostAndFoundPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirmWithModal = useConfirmation();
  const { enqueueSnackbar } = useSnackbar();
  const { labId } = useCurrentLabId();
  const { hasPermission } = usePermissions();
  const { data: studies, isLoading: studiesLoading } = useStudies(labId);
  const {
    filterModel,
    handleFilterModelChange,
    handlePageSizeChange,
    hasActiveFilters,
    missingReasons,
    pageSize,
    requestParams,
    returnPage,
    returnTargetStudyId,
    setMissingReasons,
  } = useLostAndFoundQueryParams();
  const viewKey = JSON.stringify({ missingReasons, requestParams });
  const previousViewKey = React.useRef(viewKey);
  const [paginationModelState, setPaginationModel] = React.useState<GridPaginationModel | undefined>(() =>
    returnPage === undefined ? undefined : { page: returnPage, pageSize }
  );
  const defaultPaginationModel = React.useMemo(() => ({ page: 0, pageSize }), [pageSize]);
  const [selectedTargetStudyId, setTargetStudyId] = React.useState(returnTargetStudyId);
  const [rowSelectionModelState, setRowSelectionModel] = React.useState<GridRowId[] | undefined>(undefined);
  const rowSelectionModel = rowSelectionModelState || DEFAULT_SELECTION_MODEL_STATE;
  const [selectedRowsByObjectKeyState, setSelectedRowsByObjectKey] = React.useState<
    Record<string, LabLostAndFoundUpload> | undefined
  >(undefined);
  const selectedRowsByObjectKey = selectedRowsByObjectKeyState || DEFAULT_SELECTED_ROWS_BY_OBJECT_KEY_STATE;
  const [pendingIgnoreObjectKeys, setPendingIgnoreObjectKeys] = React.useState<string[]>([]);
  const [ignoreErrorsByObjectKey, setIgnoreErrorsByObjectKey] = React.useState<Record<string, string>>({});
  const [density, setDensity] = React.useState<GridDensity>('compact');
  const [selectedUploadForExecution, setSelectedUploadForExecution] = React.useState<LabLostAndFoundUpload | undefined>(
    undefined
  );
  const lostAndFoundQuery = useLabLostAndFound(labId, requestParams);
  const uploadSummaryQuery = useLabUploadSummary(labId);
  const response = lostAndFoundQuery.data;
  const canApplyCaseManifest = hasPermission(Permission.ApplyCaseManifest);
  const canExecuteUploadIngest = hasPermission(Permission.ExecuteUploadedSlideIngest);
  const canIgnoreUploads = hasPermission(Permission.HideRestoreStudyUploads);
  const canSelectRows = canApplyCaseManifest || canIgnoreUploads;
  const targetStudies = orderBy(studies || [], ['name'], ['asc']);
  const targetStudyId =
    !studiesLoading && some(targetStudies, ({ id }) => id === selectedTargetStudyId) ? selectedTargetStudyId : '';
  const refreshRequired = lostAndFoundQuery.isError && Boolean(response);
  const selectedRows = filter(
    map(rowSelectionModel, (objectKey) => selectedRowsByObjectKey[String(objectKey)]),
    Boolean
  ) as LabLostAndFoundUpload[];
  const selectedObjectKeys = map(selectedRows, ({ objectKey }) => objectKey);
  const hasPendingSelection = pendingIgnoreObjectKeys.some((objectKey) => includes(selectedObjectKeys, objectKey));
  const hasSelectedUploadsWithoutSuccessfulIngest = selectedRows.some(({ missingIngest }) => missingIngest);
  const visibleUploads = filter(response?.lostUploads || [], (upload) =>
    missingReasons.some(
      (missingReason) =>
        (missingReason === 'missingIngest' && upload.missingIngest) ||
        (missingReason === 'missingCaseAssignment' && upload.missingCaseAssignment)
    )
  );
  // A restored page stays requested until enough rows load to show it; the grid only sees the clamped page.
  const lastPageIndex = Math.max(0, Math.ceil(visibleUploads.length / pageSize) - 1);
  const paginationModel = React.useMemo(() => {
    const requestedModel = paginationModelState || defaultPaginationModel;
    return { ...requestedModel, page: Math.min(requestedModel.page, lastPageIndex) };
  }, [defaultPaginationModel, lastPageIndex, paginationModelState]);
  const missingReasonCounts = Object.fromEntries(
    map(LOST_AND_FOUND_MISSING_REASONS, (missingReason) => [
      missingReason,
      filter(response?.lostUploads || [], (upload) => upload[missingReason]).length,
    ])
  );
  const ignoreMutation = useMutation((objectKeys: string[]) => ignoreLabUploads(labId, objectKeys));
  const quickFilterQuery = (filterModel?.quickFilterValues || []).join(' ').trim();

  React.useEffect(() => {
    if (previousViewKey.current !== viewKey) {
      previousViewKey.current = viewKey;
      setPaginationModel(undefined);
      setRowSelectionModel(undefined);
      setSelectedRowsByObjectKey(undefined);
    }
  }, [viewKey]);

  const handlePaginationModelChange = (nextPaginationModel: GridPaginationModel) => {
    if (nextPaginationModel.pageSize !== pageSize) {
      handlePageSizeChange(nextPaginationModel.pageSize);
      setPaginationModel(undefined);
      return;
    }
    if (nextPaginationModel.page === paginationModel.page) {
      return;
    }
    setPaginationModel(nextPaginationModel);
  };

  const handleRowSelectionModelChange = (nextRowSelectionModel: GridRowId[]) => {
    const lockedSelectedObjectKeys = filter(map(rowSelectionModel, String), (objectKey) =>
      includes(pendingIgnoreObjectKeys, objectKey)
    );
    const nextSelectedObjectKeys = uniq([...map(nextRowSelectionModel, String), ...lockedSelectedObjectKeys]);
    setRowSelectionModel(nextSelectedObjectKeys);
    setSelectedRowsByObjectKey((currentRows) => {
      const nextRows = Object.fromEntries(
        filter(Object.entries(currentRows || DEFAULT_SELECTED_ROWS_BY_OBJECT_KEY_STATE), ([objectKey]) =>
          includes(nextSelectedObjectKeys, objectKey)
        )
      );

      forEach(response?.lostUploads || [], (uploadRow) => {
        if (includes(nextSelectedObjectKeys, uploadRow.objectKey)) {
          nextRows[uploadRow.objectKey] = uploadRow;
        }
      });

      return nextRows;
    });
  };

  const openManifestDialogForRows = (rows: LabLostAndFoundUpload[], groupIntoSingleCase: boolean) => {
    if (refreshRequired || !targetStudyId || rows.length === 0 || rows.some(({ missingIngest }) => missingIngest)) {
      return;
    }

    const manifestDialogUrl = getManifestDialogUrlForLostUploads({
      uploadRows: rows,
      groupIntoSingleCase,
      labId,
      targetStudyId,
      returnTo: {
        origin: 'lostAndFound',
        labId,
        studyId: targetStudyId,
        page: paginationModel.page + 1,
        pageSize,
        searchTerm: quickFilterQuery || undefined,
        missingReasons,
        sourceStudyIds: requestParams.sourceStudyIds,
        uploadedAtStart: requestParams.uploadedAtStart,
        uploadedAtEnd: requestParams.uploadedAtEnd,
        anchor: rows[0]?.objectKey,
      },
    });

    if (manifestDialogUrl) {
      navigate(manifestDialogUrl);
    }
  };

  const openManifestDialog = (groupIntoSingleCase: boolean) =>
    openManifestDialogForRows(selectedRows, groupIntoSingleCase);

  const invalidateIgnoreQueries = async (sourceStudyIdsToInvalidate: string[]) => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['labUploadSummary', labId], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['labLostAndFound', labId], exact: false }),
      ...map(uniq(sourceStudyIdsToInvalidate), (sourceStudyId) =>
        queryClient.invalidateQueries({ queryKey: ['studyUploads', sourceStudyId, labId], exact: false })
      ),
      queryClient.invalidateQueries({ queryKey: ['jobs'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['jobsFilteredIds'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['pendingSlides'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['pendingSlidesCount'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['slideCaseAssignmentOptions'], exact: false }),
      queryClient.invalidateQueries({ queryKey: ['study'], exact: false }),
    ]);
  };

  const handleIgnoreUploads = async (rowsToIgnore: LabLostAndFoundUpload[]) => {
    if (!rowsToIgnore.length || rowsToIgnore.some(({ objectKey }) => includes(pendingIgnoreObjectKeys, objectKey))) {
      return;
    }

    const objectKeys = map(rowsToIgnore, ({ objectKey }) => objectKey);
    const confirmed = await confirmWithModal({
      title: `Ignore ${objectKeys.length} selected upload${objectKeys.length === 1 ? '' : 's'}?`,
      text: 'Ingest attempts will be archived and the uploads will be hidden. No files, slides, cases, or results will be deleted.',
      confirmButtonProps: {
        title: 'Ignore upload',
        color: 'warning',
      },
    });
    if (!confirmed) {
      return;
    }

    setPendingIgnoreObjectKeys((currentObjectKeys) => uniq([...currentObjectKeys, ...objectKeys]));
    setIgnoreErrorsByObjectKey((currentErrors) =>
      Object.fromEntries(filter(Object.entries(currentErrors), ([objectKey]) => !includes(objectKeys, objectKey)))
    );
    await queryClient.cancelQueries({ queryKey: ['labLostAndFound', labId], exact: false });
    const previousLostAndFoundQueries = queryClient.getQueriesData<InfiniteData<LabLostAndFoundResponse>>({
      queryKey: ['labLostAndFound', labId],
    });
    queryClient.setQueriesData<InfiniteData<LabLostAndFoundResponse>>(
      { queryKey: ['labLostAndFound', labId] },
      (currentResponse) => removeUploadsFromInfiniteResponse(currentResponse, objectKeys)
    );

    try {
      const { outcomes } = await ignoreMutation.mutateAsync(objectKeys);
      const successfulObjectKeys = map(
        filter(outcomes, ({ success }) => success),
        ({ objectKey }) => objectKey
      );
      const failedOutcomes = filter(outcomes, ({ success }) => !success);
      const failedObjectKeys = map(failedOutcomes, ({ objectKey }) => objectKey);

      forEach(previousLostAndFoundQueries, ([queryKey, previousResponse]) => {
        queryClient.setQueryData(queryKey, removeUploadsFromInfiniteResponse(previousResponse, successfulObjectKeys));
      });
      setRowSelectionModel((currentSelection) =>
        filter(
          currentSelection,
          (objectKey) => !includes(objectKeys, String(objectKey)) || includes(failedObjectKeys, String(objectKey))
        )
      );
      setSelectedRowsByObjectKey((currentRows) =>
        Object.fromEntries(
          filter(
            Object.entries(currentRows || DEFAULT_SELECTED_ROWS_BY_OBJECT_KEY_STATE),
            ([objectKey]) => !includes(objectKeys, objectKey) || includes(failedObjectKeys, objectKey)
          )
        )
      );
      setIgnoreErrorsByObjectKey((currentErrors) => ({
        ...currentErrors,
        ...Object.fromEntries(
          map(failedOutcomes, ({ objectKey, error }) => [objectKey, error || 'Unable to ignore this upload.'])
        ),
      }));

      if (failedOutcomes.length === 0) {
        enqueueSnackbar(
          `${successfulObjectKeys.length} upload${successfulObjectKeys.length === 1 ? '' : 's'} ignored.`,
          {
            variant: 'success',
          }
        );
      } else {
        enqueueSnackbar(
          `${successfulObjectKeys.length} ignored; ${failedOutcomes.length} could not be ignored and remain selected.`,
          { variant: successfulObjectKeys.length ? 'warning' : 'error' }
        );
      }
    } catch (error) {
      forEach(previousLostAndFoundQueries, ([queryKey, previousResponse]) => {
        queryClient.setQueryData(queryKey, previousResponse);
      });
      const errorMessage = getQueryErrorMessage(error, 'Unable to ignore the selected uploads.');
      setIgnoreErrorsByObjectKey((currentErrors) => ({
        ...currentErrors,
        ...Object.fromEntries(map(objectKeys, (objectKey) => [objectKey, errorMessage])),
      }));
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setPendingIgnoreObjectKeys((currentObjectKeys) => difference(currentObjectKeys, objectKeys));
      await invalidateIgnoreQueries(map(rowsToIgnore, ({ sourceStudyId }) => sourceStudyId));
    }
  };

  const columns = useLostAndFoundColumns({
    canApplyCaseManifest,
    canAssignToTargetStudy: Boolean(targetStudyId),
    canExecuteUploadIngest,
    canIgnoreUploads,
    ignoreErrorsByObjectKey,
    labId,
    onAssignUpload: (upload) => openManifestDialogForRows([upload], false),
    onExecuteUploadIngest: setSelectedUploadForExecution,
    onIgnoreUpload: (upload) => void handleIgnoreUploads([upload]),
    pendingIgnoreObjectKeys,
    quickFilterQuery,
    studies: studies || [],
    lifecycleRequestState: refreshRequired ? 'stale' : 'ready',
  });

  const hiddenOnlyEmptyState =
    !hasActiveFilters &&
    visibleUploads.length === 0 &&
    !response?.hasMore &&
    (uploadSummaryQuery.data?.statistics.hiddenSlides || 0) > 0;

  return (
    <Stack sx={{ pt: 2, height: '100%', minHeight: 0 }}>
      <PageHeader
        pageTitle="Lost and found slides"
        pageSubtitle={
          <Typography variant="body2" color="text.secondary">
            Uploaded slides that still need ingest or a case assignment
          </Typography>
        }
        onBackClick={() => navigate(`/?labId=${encodeURIComponent(labId)}`)}
      />

      {lostAndFoundQuery.isError && !response ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={() => lostAndFoundQuery.refetch()}>
              Retry
            </Button>
          }
          sx={{ mt: 2 }}
        >
          Lost and found slides are unavailable. Studies and uploaded files remain available.
        </Alert>
      ) : (
        <Stack sx={{ mt: 2, flex: 1, minHeight: 0 }} spacing={1}>
          {lostAndFoundQuery.isError && response ? (
            <Alert
              severity="warning"
              action={
                <Button color="inherit" size="small" onClick={() => lostAndFoundQuery.refetch()}>
                  Retry
                </Button>
              }
            >
              Status changed. Refresh before assigning or ingesting an upload.
            </Alert>
          ) : null}
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
            <CountedToggleButtonGroup
              value={missingReasons}
              onChange={(nextReasons) => setMissingReasons(nextReasons as typeof missingReasons)}
              options={map(LOST_AND_FOUND_MISSING_REASONS, (missingReason) => ({
                value: missingReason,
                label: missingReasonLabels[missingReason],
                count: missingReasonCounts[missingReason],
              }))}
            />
            {lostAndFoundQuery.isProgressivelyLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">
                  Loading all lab uploads... {response?.lostUploads.length || 0} loaded
                </Typography>
              </Stack>
            ) : null}
          </Stack>
          <Paper sx={{ width: '100%', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            <ReadOnlyDataGrid
              aria-label="Lost and found uploads grid"
              rows={visibleUploads}
              columns={columns}
              getRowId={(row) => row.objectKey}
              loading={lostAndFoundQuery.isLoading}
              pagination
              paginationMode="client"
              filterMode="server"
              sortingMode="client"
              paginationModel={paginationModel}
              onPaginationModelChange={handlePaginationModelChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              filterModel={filterModel || DEFAULT_READ_ONLY_GRID_FILTER_MODEL}
              onFilterModelChange={handleFilterModelChange}
              hasActiveFilters={hasActiveFilters}
              disableRowSelectionOnClick
              checkboxSelection={canSelectRows}
              isRowSelectable={({ id }) => !includes(pendingIgnoreObjectKeys, String(id))}
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={(nextRowSelectionModel) =>
                handleRowSelectionModelChange(nextRowSelectionModel as GridRowId[])
              }
              keepNonExistentRowsSelected
              hideFooterSelectedRowCount
              density={density}
              onDensityChange={setDensity}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'uploadedAt', sort: 'desc' }],
                },
                pagination: {
                  paginationModel: defaultPaginationModel,
                },
              }}
              disableVirtualization={DISABLE_VIRTUALIZATION_FOR_TESTS}
              getRowHeight={() => 'auto'}
              noRowsTitle={hasActiveFilters ? 'No unresolved slides match these filters' : 'No unresolved slides'}
              noRowsSubtitle={
                hiddenOnlyEmptyState ? (
                  <>
                    Hidden uploads are excluded here. Choose a study from{' '}
                    <Link component={RouterLink} to={`/?labId=${encodeURIComponent(labId)}`}>
                      Studies
                    </Link>
                    , then open its Uploads dashboard to review or restore them.
                  </>
                ) : hasActiveFilters ? (
                  'Try changing or clearing the active filters.'
                ) : (
                  'Every visible uploaded slide has been reconciled.'
                )
              }
              slotProps={{
                toolbar: {
                  quickFilterPosition: 'belowFilters',
                  toolbarActions: (
                    <Stack spacing={1} alignItems="flex-end">
                      {response?.lostUploads.length ? (
                        <LostAndFoundToolbar
                          canApplyCaseManifest={canApplyCaseManifest}
                          canIgnoreUploads={canIgnoreUploads}
                          hasPendingSelection={hasPendingSelection}
                          hasSelectedUploadsWithoutSuccessfulIngest={hasSelectedUploadsWithoutSuccessfulIngest}
                          onCreateCases={openManifestDialog}
                          onIgnoreUploads={() => void handleIgnoreUploads(selectedRows)}
                          onTargetStudyChange={setTargetStudyId}
                          refreshRequired={refreshRequired}
                          selectedRowCount={selectedRows.length}
                          studiesLoading={studiesLoading}
                          targetStudies={targetStudies}
                          targetStudyId={targetStudyId}
                          visibleUploadCount={visibleUploads.length}
                        />
                      ) : null}
                    </Stack>
                  ),
                },
              }}
              sx={{
                '& .MuiDataGrid-cell': { py: 1 },
                '& .MuiDataGrid-cellContent': { whiteSpace: 'normal' },
              }}
            />
          </Paper>
        </Stack>
      )}
      {selectedUploadForExecution ? (
        <UploadIngestExecutionDialog
          row={toUploadDashboardRow(selectedUploadForExecution)}
          studyId={selectedUploadForExecution.sourceStudyId}
          labId={labId}
          onClose={() => {
            setSelectedUploadForExecution(undefined);
            void queryClient.invalidateQueries({ queryKey: ['labLostAndFound', labId], exact: false });
            void queryClient.invalidateQueries({ queryKey: ['labUploadSummary', labId], exact: false });
          }}
        />
      ) : null}
    </Stack>
  );
};

export default wrapPage(
  LostAndFoundPage,
  {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    py: 0,
  },
  false,
  {
    bodySx: {
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      overflow: 'hidden',
    },
  }
);
